import { useEffect, useState, useRef } from 'react'
import { useAccountContext } from '../contexts'
import { api } from '../services'
import { roundToDecimal } from '../common/utils'
import { SensorPort, UnitCategoryId } from '../common/enums'
import { convertBaseToDisplay } from '../common/conversions'
import { Spinner } from './Spinner'
import { getUserUnitSetting } from '../utils/unitSettings'

interface LastReadingData {
  productName: string
  readings: Array<string>
}

let infoWindow: google.maps.InfoWindow

// Type definitions for the marker library
declare global {
  namespace google.maps {
    namespace marker {
      class AdvancedMarkerElement {
        constructor(options?: any)
        position: google.maps.LatLng | google.maps.LatLngLiteral | null
        map: google.maps.Map | null
        title: string
        content: HTMLElement | null
        addListener(event: string, handler: () => void): void
      }
      class PinElement {
        constructor(options?: any)
        element: HTMLElement
      }
    }
  }
}

export function Marker(options: any) {
  const selectDevice: (sigfoxId: string) => void = options.selectDevice
  const selected = options.selected
  const markerColor = options.markerColor // Color from device status

  const { userUnitSettings, unitConversions, selectedDevice, devices } = useAccountContext()

  // The marker is created ONCE and reused. Previously a new AdvancedMarkerElement
  // was constructed on every render and assigned to the map without the previous
  // one ever being removed, so every re-render stacked another marker per device
  // onto the map. It also meant the marker handed to infoWindow.open() below was
  // a brand new element that had not been attached to a map yet, which is what
  // produced "InfoWindow.open() was called without an associated Map".
  const markerRef = useRef<any>(null)
  if (markerRef.current === null) {
    markerRef.current = new (google.maps as any).marker.AdvancedMarkerElement()
  }
  const marker = markerRef.current

  infoWindow = infoWindow ?? new google.maps.InfoWindow()

  // const [device, setDevice] = useState<DeviceData>(options.device)
  const [lastReadings, setLastReadings] = useState<Record<string, LastReadingData>>({})
  const [lastReadingTimestamp, setLastReadingTimestamp] = useState<Date>(new Date())
  const [isAquaTrollSensor, setIsAquaTrollSensor] = useState(false)
  const [isWeatherStation, setIsWeatherStation] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const device = devices[options.sigfoxId]

  // Track if we've already fetched for the current selection to prevent duplicate calls
  const lastFetchedDeviceRef = useRef<string | null>(null)
  const isThisDeviceSelected = selectedDevice === device.sigfoxId && selected

  useEffect(() => {
    // Only fetch when THIS device becomes selected AND we haven't already fetched for it
    // This prevents all markers from refetching when userUnitSettings/unitConversions change
    if (
      isThisDeviceSelected &&
      userUnitSettings !== undefined &&
      unitConversions !== undefined &&
      lastFetchedDeviceRef.current !== device.sigfoxId
    ) {
      lastFetchedDeviceRef.current = device.sigfoxId
      setIsLoading(true)
      api
        .getLastReadings(device.sigfoxId)
        .then((result: any) => {
          const lastReadings = formatLastReadings(result.data)
          setIsAquaTrollSensor(checkForAquaTroll(lastReadings))
          setIsWeatherStation(checkForWeatherStation(lastReadings))

          setLastReadings(lastReadings)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    // Reset the ref when this device is deselected so it can fetch again next time
    if (!isThisDeviceSelected && lastFetchedDeviceRef.current === device.sigfoxId) {
      lastFetchedDeviceRef.current = null
    }

    return () => {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isThisDeviceSelected, device.sigfoxId, userUnitSettings, unitConversions])

  // Keep the click handler pointing at the latest props without having to
  // tear down and re-register the listener on every render.
  const clickRef = useRef<() => void>(() => {})
  clickRef.current = () => selectDevice(device.sigfoxId)

  useEffect(() => {
    const listener = (marker as any).addListener('click', () => clickRef.current())
    return () => listener?.remove?.()
  }, [marker])

  // Detach the marker from the map when this component goes away.
  useEffect(() => {
    return () => {
      marker.map = null
    }
  }, [marker])

  useEffect(() => {
    marker.position = options.position
    marker.map = options.map ?? null

    // Only set title if it's defined and is a string
    if (options.title && typeof options.title === 'string') {
      marker.title = options.title
    }

    // Apply marker color based on device status using PinElement for AdvancedMarkerElement
    if (markerColor) {
      const pinElement = new (google.maps as any).marker.PinElement({
        background: markerColor,
        borderColor: '#FFFFFF',
        glyphColor: '#FFFFFF',
        scale: 1.2,
      })
      marker.content = pinElement.element
    }
  }, [marker, options.position, options.map, options.title, markerColor])

  const isSelected = device.sigfoxId === selectedDevice

  useEffect(() => {
    // `infoWindow` is a module-level singleton shared by every Marker. Only the
    // selected marker may touch it -- previously every marker called setContent()
    // and close() on each render, so the last marker to render clobbered the
    // selected one's content. Waiting for `options.map` also matters: opening
    // against an anchor that is not attached to a map is what logged
    // "InfoWindow.open() was called without an associated Map".
    if (!isSelected || !options.map) {
      return
    }

    let htmlContent = ''

    if (isLoading) {
      htmlContent += `
      <div style="width: 210px">
        <b>${device.deviceName}</b>
        <br>Site: ${device.siteName}
        <br>Last Reading: ...
        <br>A1: ...
        <br>A2: ...
        <br>B1: ...
        <br>B2: ...
        <br>C1: ...
        <br>C2: ...
    `
      infoWindow.setContent(htmlContent)
      infoWindow.open({
        map: options.map,
        anchor: marker,
        shouldFocus: true,
      })
      return () => {
        infoWindow.close()
      }
    }

    htmlContent += `
      <div>
        <b>${device.deviceName}</b>
        <br>Site: ${device.siteName}
        <br>Last Reading: ${lastReadingTimestamp.toLocaleString()}
        <br>${getSensorData(SensorPort.A1)}
        <br>${getSensorData(SensorPort.A2)}
        <br>${getSensorData(SensorPort.B1)}
        <br>${getSensorData(SensorPort.B2)}
        <br>${getSensorData(SensorPort.C1)}
        <br>${getSensorData(SensorPort.C2)}
    `

    if (isAquaTrollSensor) {
      htmlContent += `<br>${getSensorData(SensorPort.C3)}`
    }

    if (isWeatherStation) {
      htmlContent += `<br>${getSensorData(SensorPort.T0)}`
    }

    htmlContent += '</div>'

    infoWindow.setContent(htmlContent)
    infoWindow.open({
      map: options.map,
      anchor: marker,
      shouldFocus: true,
    })

    function getSensorData(sensorPort: SensorPort) {
      const port = sensorPort === SensorPort.C3 ? 'T0' : sensorPort

      if (lastReadings[port] && lastReadings[port].readings.length > 0) {
        let productName = lastReadings[port].productName
        if (isAquaTrollSensor && sensorPort === SensorPort.C3) {
          productName = 'AquaTROLL Water Temperature'
        }
        return `${sensorPort === 'T0' ? 'C3' : sensorPort}: ${productName} -- ${lastReadings[port].readings.join(', ')}`
      }
      return `${sensorPort === 'T0' ? 'C3' : sensorPort}: Not Connected`
    }
    // Close the shared window when this marker stops being the selected one
    // (or unmounts), since no other marker will do it for us now.
    return () => {
      infoWindow.close()
    }
  }, [isSelected, options.map, isLoading, device, lastReadings, lastReadingTimestamp, isAquaTrollSensor, isWeatherStation]) // eslint-disable-line react-hooks/exhaustive-deps

  function formatLastReadings(response: any) {
    const result: Record<string, LastReadingData> = {}

    const isAquaTrollSensor = response['A1-21'] !== undefined

    Object.keys(response).forEach((sensorPortAndUnitCategoryId: string) => {
      const [sensorPort] = sensorPortAndUnitCategoryId.split('-')
      const { readings, metadata } = response[sensorPortAndUnitCategoryId]

      if (result[sensorPort] === undefined) {
        result[sensorPort] = {
          productName: metadata.productName,
          readings: [],
        }
      }

      if (Object.keys(readings).length > 0) {
        const timestamp = Number(Object.keys(readings).reverse()[0])

        setLastReadingTimestamp(new Date(timestamp * 1000))

        let unitCategoryId = metadata.units.category.id

        if (isAquaTrollSensor && sensorPort === 'T0') {
          unitCategoryId = UnitCategoryId.WaterTemperature
        }

        const { unitId, precision } = getUserUnitSetting(unitCategoryId, userUnitSettings, unitConversions)
        const unit = unitConversions[unitCategoryId as UnitCategoryId].abbreviations[unitId]
        const formula = unitConversions[unitCategoryId as UnitCategoryId].formulas[unitId]
        let value = 0

        if (isAquaTrollSensor && sensorPort === 'T0') {
          value = Number(Object.values(readings).reverse()[0])
          value = (value + 40) * 32 // reverse old temperature conversion
          value = value / 50 - 5 // use AquaTroll500TemperatureSensor as per specification (even though the C3 scaling parameters are not sent from the firmware)
          value = roundToDecimal(value, precision)
        } else {
          value = Number(Object.values(readings).reverse()[0])
        }

        const lastReading = roundToDecimal(convertBaseToDisplay(value, formula), precision)

        result[sensorPort].readings.push(`${lastReading} ${unit}`)
      }
    })

    return result
  }

  function checkForAquaTroll(lastReadings: any) {
    const checks: Record<string, boolean> = {
      'aquatroll dissolved oxygen': false,
      'aquatroll turbidity': false,
      'aquatroll nitrate': false,
      'aquatroll salinity': false,
      'aquatroll total dissolved solids': false,
      'aquatroll specific conductivity': false,
    }

    Object.values(lastReadings)
      .map((item: any) => item.productName.toLowerCase())
      .forEach((productName: string) => {
        if (checks[productName] !== undefined) {
          checks[productName] = true
        }
      })

    return Object.values(checks).every((value) => value === true)
  }

  function checkForWeatherStation(lastReadings: any) {
    const checks: Record<string, boolean> = {
      'wind speed': false,
      'wind direction': false,
      'solar radiation': false,
      rainfall: false,
      pressure: false,
      humidity: false,
    }

    Object.values(lastReadings)
      .map((item: any) => item.productName.toLowerCase())
      .forEach((productName: string) => {
        if (checks[productName] !== undefined) {
          checks[productName] = true
        }
      })

    return Object.values(checks).every((value) => value === true)
  }

  // right align
  return <div className="flex flex-row-reverse">{isLoading ? <Spinner size={25} /> : null}</div>
}
